import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Users,
  Mic,
  MicOff,
  PhoneOff,
  Phone,
} from "lucide-react";
import socket from "../utils/socket";
import useVoiceCall from "../hooks/useVoiceCall";
import  toast  from "react-hot-toast";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Bebas+Neue&display=swap');

  * { box-sizing: border-box; }

  .vc-username-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    min-height: 100dvh;
    background-color: #111;
    background-image: repeating-linear-gradient(0deg,transparent,transparent 39px,#1e1e1e 39px,#1e1e1e 40px),
      repeating-linear-gradient(90deg,transparent,transparent 39px,#1e1e1e 39px,#1e1e1e 40px);
    padding: 32px 20px;
    font-family: 'Space Mono', monospace;
  }

  .vc-login-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(4.5rem, 20vw, 6rem);
    color: #ff3c00;
    text-shadow: 5px 5px 0 #ffd000;
    margin-bottom: 8px;
    letter-spacing: 0.05em;
    text-align: center;
  }

  .vc-login-sub {
    font-size: clamp(0.65rem, 3vw, 0.75rem);
    font-weight: 700;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: #ffd000;
    border: 2px solid #ffd000;
    padding: 5px 14px;
    margin-bottom: 32px;
    background: transparent;
  }

  .vc-login-card {
    background: #1a1a1a;
    border: 3px solid #ffd000;
    box-shadow: 8px 8px 0 #ffd000;
    padding: 32px 24px;
    width: 100%;
    max-width: 440px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .vc-login-label {
    font-size: clamp(0.65rem, 3vw, 0.75rem);
    font-weight: 700;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: #aaa;
    margin-bottom: 6px;
    display: block;
  }

  .vc-login-input {
    width: 100%;
    padding: 18px 16px;
    font-family: 'Space Mono', monospace;
    font-size: 16px;
    font-weight: 700;
    border: 3px solid #444;
    background: #111;
    color: #fff;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    -webkit-tap-highlight-color: transparent;
  }
  .vc-login-input:focus { border-color: #ffd000; box-shadow: 4px 4px 0 #ffd000; }
  .vc-login-input::placeholder { color: #555; font-weight: 400; }

  .vc-login-btn {
    background: #ff3c00;
    color: #fff;
    border: 3px solid #ffd000;
    box-shadow: 5px 5px 0 #ffd000;
    padding: 20px;
    font-family: 'Space Mono', monospace;
    font-size: clamp(0.9rem, 4vw, 1.05rem);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    cursor: pointer;
    transition: transform 0.1s, box-shadow 0.1s;
    touch-action: manipulation;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    -webkit-tap-highlight-color: transparent;
  }
  .vc-login-btn:hover { transform: translate(-2px,-2px); box-shadow: 7px 7px 0 #ffd000; }
  .vc-login-btn:active { transform: translate(3px,3px); box-shadow: 2px 2px 0 #ffd000; }

  .vc-outer {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    background: #111;
    display: flex;
    justify-content: center;
    align-items: stretch;
  }

  .vc-root {
    font-family: 'Space Mono', monospace;
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    width: 80%;
    max-width: 100%;
    background: #111;
    background-image: repeating-linear-gradient(0deg,transparent,transparent 39px,#1a1a1a 39px,#1a1a1a 40px),
      repeating-linear-gradient(90deg,transparent,transparent 39px,#1a1a1a 39px,#1a1a1a 40px);
  }

  .vc-header {
    background: #1a1a1a;
    border-bottom: 3px solid #ffd000;
    padding: 14px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    z-index: 30;
  }

  .vc-header-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }

  .vc-back-btn {
    background: transparent;
    border: 2px solid #ff3c00;
    color: #ff3c00;
    padding: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
    flex-shrink: 0;
    touch-action: manipulation;
  }
  .vc-back-btn:hover { background: #ff3c00; color: #fff; }

  .vc-room-info { display: flex; flex-direction: column; min-width: 0; }

  .vc-room-name {
    color: #ffd000;
    font-size: clamp(0.8rem, 3.5vw, 1rem);
    font-weight: 700;
    letter-spacing: 0.05em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .vc-copy-btn {
    background: transparent;
    border: none;
    color: #aaa;
    cursor: pointer;
    padding: 2px;
    display: flex;
    flex-shrink: 0;
  }
  .vc-copy-btn:hover { color: #ffd000; }

  .vc-voice-label {
    color: #ff3c00;
    font-size: clamp(0.55rem, 2.2vw, 0.65rem);
    font-weight: 700;
    letter-spacing: 0.3em;
    text-transform: uppercase;
  }

  .vc-users-count {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #aaa;
    font-size: clamp(0.7rem, 3vw, 0.8rem);
    font-weight: 700;
    letter-spacing: 0.1em;
    background: #111;
    border: 2px solid #333;
    padding: 8px 14px;
  }

  .vc-stage {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    gap: 32px;
    overflow-y: auto;
  }

  .vc-participants {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: center;
    max-width: 700px;
    width: 100%;
  }

  .vc-participant {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    animation: fadeInPeer 0.3s ease;
  }

  .vc-avatar {
    width: 80px;
    height: 80px;
    background: #1a1a1a;
    border: 3px solid #333;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.2rem;
    color: #ffd000;
    position: relative;
    transition: border-color 0.2s;
  }

  .vc-avatar.me { border-color: #bbff00; }
  .vc-avatar.active { border-color: #ff3c00; box-shadow: 0 0 0 3px rgba(255,60,0,0.3); }

  .vc-avatar-muted {
    position: absolute;
    bottom: -6px;
    right: -6px;
    background: #ff3c00;
    border: 2px solid #111;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .vc-peer-name {
    color: #aaa;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
  }
  .vc-peer-name.me { color: #bbff00; }

  .vc-waiting {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    opacity: 0.4;
  }

  .vc-waiting-text {
    color: #aaa;
    font-size: clamp(0.7rem, 3vw, 0.8rem);
    font-weight: 700;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    text-align: center;
  }

  .vc-idle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    text-align: center;
    padding: 40px 20px;
  }

  .vc-idle-icon { font-size: 4rem; opacity: 0.3; }

  .vc-idle-text {
    color: #555;
    font-size: clamp(0.75rem, 3vw, 0.9rem);
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    max-width: 280px;
    line-height: 1.8;
  }

  .vc-controls {
    background: #1a1a1a;
    border-top: 3px solid #222;
    padding: 24px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-shrink: 0;
  }

  .vc-ctrl-btn {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid #111;
    cursor: pointer;
    transition: transform 0.1s, box-shadow 0.1s;
    font-family: 'Space Mono', monospace;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    flex-shrink: 0;
  }

  .vc-ctrl-btn.join {
    background: #bbff00;
    color: #111;
    box-shadow: 5px 5px 0 #ffd000;
    width: auto;
    padding: 0 28px;
    gap: 10px;
    font-size: clamp(0.85rem, 3.5vw, 1rem);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-color: #111;
  }
  .vc-ctrl-btn.join:hover { transform: translate(-2px,-2px); box-shadow: 7px 7px 0 #ffd000; }
  .vc-ctrl-btn.join:active { transform: translate(3px,3px); box-shadow: 2px 2px 0 #ffd000; }

  .vc-ctrl-btn.mute {
    background: #222;
    color: #ffd000;
    box-shadow: 4px 4px 0 #ffd000;
    border-color: #ffd000;
  }
  .vc-ctrl-btn.mute.muted { background: #ffd000; color: #111; }
  .vc-ctrl-btn.mute:hover { transform: translate(-2px,-2px); box-shadow: 6px 6px 0 #ffd000; }

  .vc-ctrl-btn.leave {
    background: #ff3c00;
    color: #fff;
    box-shadow: 4px 4px 0 #ffd000;
    border-color: #ffd000;
  }
  .vc-ctrl-btn.leave:hover { transform: translate(-2px,-2px); box-shadow: 6px 6px 0 #ffd000; }
  .vc-ctrl-btn.leave:active { transform: translate(3px,3px); box-shadow: 2px 2px 0 #ffd000; }

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(0.85); }
  }

  @keyframes fadeInPeer {
    from { opacity: 0; transform: scale(0.85); }
    to { opacity: 1; transform: scale(1); }
  }

  @media (max-width: 600px) {
    .vc-root { width: 100%; }
    .vc-ctrl-btn { width: 56px; height: 56px; }
    .vc-ctrl-btn.join { padding: 0 20px; }
    .vc-avatar { width: 68px; height: 68px; font-size: 1.8rem; }
  }
`;

const RemoteAudio = ({ stream }) => {
  const audioRef = useRef(null);
  useEffect(() => {
    if (audioRef.current && stream) {
      audioRef.current.srcObject = stream;
    }
  }, [stream]);
  return <audio ref={audioRef} autoPlay playsInline />;
};

function VoiceRoom() {
  const navigate = useNavigate();
  const { roomId } = useParams();

  // Pre-fill username for convenience, but ALWAYS show login screen on every visit
  const [username, setUsername] = useState(
    () => sessionStorage.getItem("vc-username") || "",
  );
  const [roomInput, setRoomInput] = useState("");
  const [isUsernameSet, setIsUsernameSet] = useState(false); // ✅ FIX: never read from sessionStorage

  // Pass username into the hook
  const { inCall, isMuted, peers, joinCall, leaveCall, toggleMute } =
    useVoiceCall(isUsernameSet ? socket : null, roomId, username);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!username.trim()) return;

    sessionStorage.setItem("vc-username", username.trim());
    setIsUsernameSet(true);

    const finalRoomId =
      roomId || roomInput.trim() || Math.random().toString(36).substring(2, 8);
    navigate(`/voice/${finalRoomId}`);
  };

  const copyRoomId = () => {
    navigator.clipboard.writeText(roomId);

    toast.success("Room ID copied!", {
      style: {
        background: "#1a1a1a",
        color: "#ffd000",
        border: "2px solid #ffd000",
        fontFamily: "Space Mono, monospace",
      },
      iconTheme: {
        primary: "#ffd000",
        secondary: "#111",
      },
    });
  };

  // ─── LOGIN SCREEN ────────────────────────────────────────────────────────
  if (!isUsernameSet) {
    return (
      <>
        <style>{styles}</style>
        <div className="vc-username-screen">
          <h2 className="vc-login-title">Chat-ME</h2>
          <div className="vc-login-sub">🎙 Voice Room</div>

          <div className="vc-login-card">
            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div>
                <label className="vc-login-label">Who are you?</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username..."
                  className="vc-login-input"
                  autoFocus
                />
              </div>

              <div>
                <label className="vc-login-label">Room ID (optional)</label>
                <input
                  type="text"
                  value={roomInput}
                  onChange={(e) => setRoomInput(e.target.value)}
                  placeholder="Enter room ID or leave empty..."
                  className="vc-login-input"
                />
                <small style={{ color: "#777", fontSize: "12px" }}>
                  Leave empty to create a new room
                </small>
              </div>

              <button type="submit" className="vc-login-btn">
                <Phone size={18} />
                Join / Create Room →
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  const totalParticipants = inCall ? peers.length + 1 : 0;

  // ─── VOICE ROOM ──────────────────────────────────────────────────────────
  return (
    <>
      <style>{styles}</style>

      {/* Remote audio elements — hidden but playing */}
      {peers.map((peer) =>
        peer.stream ? <RemoteAudio key={peer.id} stream={peer.stream} /> : null,
      )}

      <div className="vc-outer">
        <div className="vc-root">
          {/* HEADER */}
          <div className="vc-header">
            <div className="vc-header-left">
              <button
                onClick={() => {
                  leaveCall();
                  navigate("/");
                }}
                className="vc-back-btn"
              >
                <ArrowLeft size={20} />
              </button>

              <div className="vc-room-info">
                <div className="vc-room-name">
                  #{roomId}
                  <button onClick={copyRoomId} className="vc-copy-btn">
                    <Copy size={12} />
                  </button>
                </div>
                <span className="vc-voice-label">● Voice Room</span>
              </div>
            </div>

            {inCall && (
              <div className="vc-users-count">
                <Users size={14} />
                {totalParticipants}
              </div>
            )}
          </div>

          {/* STAGE */}
          <div className="vc-stage">
            {!inCall ? (
              <div className="vc-idle">
                <div className="vc-idle-icon">
                  <Mic size={80} color="#ffffff" />
                </div>
                <div className="vc-idle-text">
                  Click "Join Call" to start talking
                </div>
              </div>
            ) : (
              <div className="vc-participants">
                {/* YOU */}
                <div className="vc-participant">
                  <div className="vc-avatar me">
                    {username.charAt(0).toUpperCase()}
                    {isMuted && (
                      <div className="vc-avatar-muted">
                        <MicOff size={11} color="#fff" />
                      </div>
                    )}
                  </div>
                  <span className="vc-peer-name me">{username} (you)</span>
                </div>

                {/* PEERS */}
                {peers.map((peer) => (
                  <div key={peer.id} className="vc-participant">
                    <div className="vc-avatar active">
                      {peer.username
                        ? peer.username.charAt(0).toUpperCase()
                        : "?"}
                    </div>
                    <span className="vc-peer-name">
                      {/* ✅ Show real username instead of socket ID */}
                      {peer.username || peer.id.slice(0, 6)}
                    </span>
                  </div>
                ))}

                {peers.length === 0 && (
                  <div className="vc-waiting">
                    <div>👂</div>
                    <div className="vc-waiting-text">Waiting for others...</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CONTROLS */}
          <div className="vc-controls">
            {!inCall ? (
              <button className="vc-ctrl-btn join" onClick={joinCall}>
                <Phone size={20} />
                Join Call
              </button>
            ) : (
              <>
                <button
                  className={`vc-ctrl-btn mute${isMuted ? " muted" : ""}`}
                  onClick={toggleMute}
                >
                  {isMuted ? <MicOff size={22} /> : <Mic size={22} />}
                </button>

                <button className="vc-ctrl-btn leave" onClick={leaveCall}>
                  <PhoneOff size={22} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default VoiceRoom;
